import React from 'react';
import { Box, Stack, Typography, Button, Tooltip } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

/**
 * Generic sticky bar that appears above any list when ≥ 1 row is selected.
 * Each action is `{ key, label, icon, onClick, color, disabled, tooltip }`.
 *
 * Visibility / permission gating happens before passing the action in —
 * actions the current admin can't perform should not be in the array.
 */
const BulkActionBar = ({ selectedCount, actions = [], onClear }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');

    if (!selectedCount) return null;

    return (
        <Box
            sx={{
                position: 'sticky',
                top: 0,
                zIndex: 5,
                mb: 2,
                p: 1.5,
                borderRadius: 2,
                border: (t) => `1px solid ${t.palette.divider}`,
                bgcolor: (t) => t.palette.primary.main + '10',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                flexWrap: 'wrap',
            }}
        >
            <Typography variant="body2">
                <strong>{selectedCount}</strong>{' '}
                {isArabic ? 'محدد' : 'selected'}
            </Typography>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {actions.filter(Boolean).map((action) => {
                    const button = (
                        <span key={action.key}>
                            <Button
                                variant={action.variant || 'contained'}
                                color={action.color || 'primary'}
                                size="small"
                                startIcon={action.icon}
                                onClick={action.onClick}
                                disabled={action.disabled}
                            >
                                {action.label}
                            </Button>
                        </span>
                    );
                    return action.tooltip
                        ? (
                            <Tooltip key={action.key} title={action.tooltip} placement="top" arrow>
                                {button}
                            </Tooltip>
                        )
                        : button;
                })}

                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<CloseIcon />}
                    onClick={onClear}
                >
                    {isArabic ? 'إلغاء التحديد' : 'Clear'}
                </Button>
            </Stack>
        </Box>
    );
};

export default BulkActionBar;
